/**
 * Pie de paginación de las tablas del portal.
 *
 * Portado de `edsuite-web/src/components/ui/paginador.tsx`. Va **debajo de la
 * tabla**, dentro de la misma tarjeta, y muestra cuántas filas se ven del total,
 * el selector de filas por página y los botones para navegar (con un campo para
 * saltar a una página concreta).
 *
 * Se usa junto a `usePaginacion`, que es quien corta el listado:
 *
 *   const { visibles, ...paginacion } = usePaginacion(filas, { clave });
 *   <Paginador {...paginacion} etiqueta="comprobantes" />
 */

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Select } from '@/components/ui/select';
import { TAMANOS_PAGINA, type Paginacion } from '@/hooks/use-paginacion';
import { cn, formatNumber } from '@/lib/utils';

export type PaginadorProps = Paginacion<unknown> & {
  /** Nombre de lo que se lista, para el texto de la izquierda ("comprobantes"). */
  etiqueta?: string;
  className?: string;
};

/** Números de página que se dibujan alrededor de la actual (1 … 4 5 6 … 20). */
function paginasVisibles(pagina: number, paginas: number, alrededor = 1): (number | '…')[] {
  if (paginas <= 7) {
    return Array.from({ length: paginas }, (_, indice) => indice + 1);
  }

  const numeros = new Set<number>([1, paginas, pagina]);

  for (let salto = 1; salto <= alrededor; salto += 1) {
    numeros.add(pagina - salto);
    numeros.add(pagina + salto);
  }

  const ordenados = [...numeros]
    .filter((numero) => numero >= 1 && numero <= paginas)
    .sort((a, b) => a - b);
  const salida: (number | '…')[] = [];

  for (const numero of ordenados) {
    const anterior = salida[salida.length - 1];

    if (typeof anterior === 'number' && numero - anterior > 1) salida.push('…');
    salida.push(numero);
  }

  return salida;
}

export function Paginador({ etiqueta = 'registros', className, ...paginacion }: PaginadorProps) {
  const { total, pagina, paginas, tamano, desde, hasta, hayPaginas, irA, cambiarTamano } = paginacion;

  const [destino, setDestino] = useState('');

  // El campo «ir a» se limpia solo cuando cambia la página.
  useEffect(() => {
    setDestino('');
  }, [pagina]);

  if (total === 0) return null;

  const botonBase =
    'inline-flex size-8 items-center justify-center rounded-control border border-ink-200 bg-white text-ink-600 transition-colors hover:border-brand-300 hover:bg-brand-50/50 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-40';

  const irAlDestino = () => {
    const numero = Number(destino);

    if (Number.isInteger(numero) && numero >= 1 && numero <= paginas) irA(numero);
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 px-5 py-3',
        className,
      )}
    >
      <p className="text-[11px] text-ink-500" aria-live="polite">
        Mostrando <strong className="font-semibold text-ink-700">{formatNumber(desde)}</strong>–
        <strong className="font-semibold text-ink-700">{formatNumber(hasta)}</strong> de{' '}
        <strong className="font-semibold text-ink-700">{formatNumber(total)}</strong> {etiqueta}
      </p>

      <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
        <label className="flex items-center gap-1.5 text-[11px] text-ink-500">
          Filas por página
          <Select
            value={String(tamano)}
            onChange={({ target }) => cambiarTamano(Number(target.value))}
            className="h-8 w-[74px] px-2 text-[11px]"
            aria-label="Filas por página"
          >
            {TAMANOS_PAGINA.map((opcion) => (
              <option key={opcion} value={String(opcion)}>
                {opcion === 0 ? 'Todas' : opcion}
              </option>
            ))}
          </Select>
        </label>

        {hayPaginas ? (
          <>
            <nav className="flex items-center gap-1" aria-label="Paginación">
              <button
                type="button"
                className={botonBase}
                onClick={() => irA(1)}
                disabled={pagina <= 1}
                title="Primera página"
                aria-label="Primera página"
              >
                <ChevronsLeft className="size-4" aria-hidden="true" />
              </button>

              <button
                type="button"
                className={botonBase}
                onClick={() => irA(pagina - 1)}
                disabled={pagina <= 1}
                title="Página anterior"
                aria-label="Página anterior"
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
              </button>

              <div className="hidden items-center gap-1 sm:flex">
                {paginasVisibles(pagina, paginas).map((numero, indice) =>
                  numero === '…' ? (
                    <span
                      key={`puntos-${indice}`}
                      className="px-1 text-[11px] text-ink-400"
                      aria-hidden="true"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={numero}
                      type="button"
                      onClick={() => irA(numero)}
                      aria-current={numero === pagina ? 'page' : undefined}
                      className={cn(
                        'inline-flex h-8 min-w-8 items-center justify-center rounded-control border px-2 text-[11px] font-semibold transition-colors',
                        numero === pagina
                          ? 'border-brand-500 bg-brand-600 text-white shadow-sm'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-300 hover:bg-brand-50/50 hover:text-brand-700',
                      )}
                    >
                      {numero}
                    </button>
                  ),
                )}
              </div>

              <button
                type="button"
                className={botonBase}
                onClick={() => irA(pagina + 1)}
                disabled={pagina >= paginas}
                title="Página siguiente"
                aria-label="Página siguiente"
              >
                <ChevronRight className="size-4" aria-hidden="true" />
              </button>

              <button
                type="button"
                className={botonBase}
                onClick={() => irA(paginas)}
                disabled={pagina >= paginas}
                title="Última página"
                aria-label="Última página"
              >
                <ChevronsRight className="size-4" aria-hidden="true" />
              </button>
            </nav>

            <label className="hidden items-center gap-1.5 text-[11px] text-ink-500 md:flex">
              Ir a
              <input
                type="number"
                min={1}
                max={paginas}
                value={destino}
                onChange={(evento) => setDestino(evento.target.value)}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter') {
                    evento.preventDefault();
                    irAlDestino();
                  }
                }}
                onBlur={irAlDestino}
                placeholder={String(pagina)}
                className="h-8 w-14 rounded-control border border-ink-200 bg-white px-2 text-center text-[11px] text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                aria-label="Ir a la página"
              />
              <span className="text-ink-400">de {formatNumber(paginas)}</span>
            </label>
          </>
        ) : null}
      </div>
    </div>
  );
}
